/**
 * SubmissionRow — one carrier's Offertanfrage row in Tab 3b (P2 Placement Slice 8): carrier +
 * Nummer + status badge + Frist line (editable) + the Versand/Erinnerung Verlauf + actions.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { CarrierLogo } from './CarrierLogo';
import { SubmissionStatusBadge } from './SubmissionStatusBadge';
import { dateShortDe, dateTimeShortDe, daysUntil } from '../../lib/format';
import {
  SUBMISSION_DEACTIVATABLE_STATUSES,
  SUBMISSION_DECLINABLE_STATUSES,
  SUBMISSION_FRIST_EDITABLE_STATUSES,
  SUBMISSION_KEINE_EMAIL_HINWEIS,
  SUBMISSION_REMINDABLE_STATUSES,
} from '@brokly/shared';
import type { SubmissionEintrag } from '@brokly/shared';

function fristHint(frist: string | null): string {
  const d = daysUntil(frist);
  if (d === null) return '';
  if (d === 0) return 'heute';
  return d > 0 ? `in ${d} Tagen` : `vor ${Math.abs(d)} Tagen`;
}

/** The Ü3b Verlauf line: «versandt DD.MM · erinnert DD.MM HH:mm · eingegangen DD.MM». */
function verlaufParts(item: SubmissionEintrag): string[] {
  const parts: string[] = [];
  const versand = item.emails.find((e) => e.art === 'versand');
  if (versand) parts.push(`versandt ${dateShortDe(versand.sent_at)}`);
  for (const e of item.emails.filter((x) => x.art === 'erinnerung')) {
    parts.push(`erinnert ${dateTimeShortDe(e.sent_at)}`);
  }
  if (item.received_at) parts.push(`eingegangen ${dateShortDe(item.received_at)}`);
  return parts;
}

export function SubmissionRow({
  item,
  canWrite,
  canSend,
  aktionErlaubt,
  onErinnern,
  onNichtOfferiert,
  onInaktiv,
  onUnterlagen,
  onFrist,
  onVersandAufloesen,
}: {
  item: SubmissionEintrag;
  canWrite: boolean;
  canSend: boolean;
  aktionErlaubt: boolean;
  onErinnern: () => void;
  onNichtOfferiert: () => void;
  onInaktiv: () => void;
  onUnterlagen: () => void;
  onFrist: (fristDate: string) => void;
  onVersandAufloesen?: (aktion: 'nachtragen' | 'freigeben') => void;
}) {
  const [editFrist, setEditFrist] = useState(false);
  const [fristDraft, setFristDraft] = useState(item.frist_date ?? '');

  const remindable = (SUBMISSION_REMINDABLE_STATUSES as readonly string[]).includes(item.status);
  const declinable = (SUBMISSION_DECLINABLE_STATUSES as readonly string[]).includes(item.status);
  const deactivatable = (SUBMISSION_DEACTIVATABLE_STATUSES as readonly string[]).includes(item.status);
  const fristEditable = (SUBMISSION_FRIST_EDITABLE_STATUSES as readonly string[]).includes(item.status);
  const parts = verlaufParts(item);

  return (
    <div className="flex flex-col gap-1 rounded-md border border-border p-3" data-testid={`sub-row-${item.carrier_uid}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CarrierLogo name={item.name} logoUrl={item.logo_url} size="sm" />
          <span className="font-medium">{item.name ?? item.carrier_uid}</span>
          <span className="mono-num text-xs text-muted-foreground">{item.submission_number}</span>
          <SubmissionStatusBadge status={item.status} fristAbgelaufen={item.frist_abgelaufen} />
        </div>
        <div className="flex items-center gap-2">
          {remindable && canSend && (
            <Button size="sm" variant="ghost" onClick={onErinnern} data-testid={`sub-erinnern-${item.carrier_uid}`}>
              Erinnern
            </Button>
          )}
          {declinable && canWrite && (
            <Button size="sm" variant="ghost" onClick={onNichtOfferiert} data-testid={`sub-nicht-offeriert-${item.carrier_uid}`}>
              nicht offeriert
            </Button>
          )}
          {deactivatable && canWrite && aktionErlaubt && (
            <Button size="sm" variant="ghost" onClick={onInaktiv} data-testid={`sub-inaktiv-${item.carrier_uid}`}>
              inaktiv
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onUnterlagen} data-testid={`sub-unterlagen-${item.carrier_uid}`}>
            Unterlagen
          </Button>
        </div>
      </div>

      {item.frist_date && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {editFrist ? (
            <>
              <input
                type="date"
                className="rounded-md border border-border bg-background p-1"
                value={fristDraft}
                onChange={(e) => setFristDraft(e.target.value)}
                data-testid={`sub-frist-input-${item.carrier_uid}`}
              />
              <Button
                size="sm"
                onClick={() => {
                  onFrist(fristDraft);
                  setEditFrist(false);
                }}
              >
                Speichern
              </Button>
            </>
          ) : (
            <>
              <span>
                Offerte erwartet bis {dateShortDe(item.frist_date)} ({fristHint(item.frist_date)})
              </span>
              {fristEditable && canWrite && aktionErlaubt && (
                <button
                  type="button"
                  className="underline"
                  onClick={() => setEditFrist(true)}
                  data-testid={`sub-frist-edit-${item.carrier_uid}`}
                >
                  ändern
                </button>
              )}
            </>
          )}
        </div>
      )}

      {parts.length > 0 && (
        <div className="text-xs text-muted-foreground" data-testid={`sub-verlauf-${item.carrier_uid}`}>
          {parts.join(' · ')}
        </div>
      )}

      {item.status === 'neu' && !item.submission_faehig && (
        <p className="text-xs text-status-warning" role="note">
          {SUBMISSION_KEINE_EMAIL_HINWEIS}
        </p>
      )}

      {item.versand_laeuft && (
        <p className="text-xs text-muted-foreground" data-testid={`sub-versand-laeuft-${item.carrier_uid}`}>
          Versand läuft …
        </p>
      )}

      {item.versand_unterbrochen && !item.versand_gesperrt && (
        <p className="text-xs text-status-danger" data-testid={`sub-versand-unterbrochen-${item.carrier_uid}`}>
          Versand unterbrochen - erneut versuchen
        </p>
      )}

      {item.versand_gesperrt && (
        <div className="space-y-1" data-testid={`sub-versand-gesperrt-${item.carrier_uid}`}>
          <p className="text-xs text-status-danger">E-Mail gesendet, aber nicht erfasst. Bitte auflösen.</p>
          <div className="flex gap-2">
            {item.nachtragen_moeglich && (
              <Button size="sm" variant="secondary" disabled={!canWrite} onClick={() => onVersandAufloesen?.('nachtragen')} data-testid={`sub-nachtragen-${item.carrier_uid}`}>
                Als versandt erfassen
              </Button>
            )}
            <Button size="sm" variant="secondary" disabled={!canWrite} onClick={() => onVersandAufloesen?.('freigeben')} data-testid={`sub-freigeben-${item.carrier_uid}`}>
              Erneuten Versand freigeben
            </Button>
          </div>
        </div>
      )}

      {!item.versand_laeuft && !item.versand_unterbrochen && !item.versand_gesperrt && item.letzter_versandfehler && (
        <p className="text-xs text-status-danger" data-testid={`sub-fehler-${item.carrier_uid}`}>
          Letzter Versand fehlgeschlagen: {item.letzter_versandfehler}
        </p>
      )}
    </div>
  );
}
